import { useState, useEffect, useMemo } from 'react'
import { numberFormat } from 'shared/util'

import {
  useUserSession
} from 'features/session/context'

import {
  indexedDB,
  useLiveQuery,
  KojinListDownloadParams,
  KojinListTargetTypes,
  DownloadFormatTypes
} from 'shared/indexedDb'

import { KojinPptxFormatList } from 'features/kojin/report/pptx'
import { KojinPrintFormatList } from 'features/kojin/report/print'

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Stack,
  Tooltip,
  IconButton,
  Button,
  Typography,
  Select,
  MenuItem,
  TextField,
  RadioGroupProps,
} from '@mui/material'

import { NumericInput } from 'components/controls/numbericInput'
import { Section } from 'components/controls/section'
import { Loading } from 'components/controls/loading'

import { OkIcon, CloseIcon } from 'shared/icons'
import { ColumnDefs } from '../constants'

import { EditKojinListColumnsDialog } from './editListColumnDialog'

type RadioGroupBoxProps = {
  list: {
    value: string
    label: string
    subLabel?: string
  }[]
} & RadioGroupProps
const RadioGroupBox = ({
  list,
  ...others
}: RadioGroupBoxProps) => {
  return (
    <RadioGroup {...others}>
      {list.map(({ value, label, subLabel }, i) => (
        <FormControlLabel
          key={`${others.name ?? 'choice'}-${i}-${value}`}
          value={value}
          control={<Radio />}
          label={(
            <Stack direction="column">
              <Typography
                variant="body1"
                color="text.primary"
              >
                {label}
              </Typography>
              {subLabel && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {subLabel}
                </Typography>
              )}
            </Stack>
          )}
        />
      ))}
    </RadioGroup>
  )
}

type KojinListDownloadDialogProps = {
  downloading: boolean
  downloadProgress: number | null
  targetCount: number
  extendCount: number
  defaultTitle: string
  onDownload: (
    title: string,
    params: KojinListDownloadParams,
    from: number, to: number
  ) => void
  onClose: () => void
}
export const KojinListDownloadDialog = ({
  downloading,
  downloadProgress,
  targetCount,
  extendCount,
  defaultTitle,
  onDownload,
  onClose
}: KojinListDownloadDialogProps) => {
  const { domain, userId } = useUserSession()

  const [title, setTitle] = useState<string>(defaultTitle)
  const [target, setTarget] = useState<KojinListTargetTypes>('all')
  const [format, setFormat] = useState<DownloadFormatTypes>('excel')
  const [reportId, setReportId] = useState<string | null>('default')
  const [columns, setColumns] = useState<string[]>(['kojinCode'])
  const [from, setFrom] = useState<string>('')
  const [to, setTo] = useState<string>((extendCount + targetCount).toString())

  const [editMode, setEditMode] = useState<'columns' | null>(null);

  const preParams = useLiveQuery<
    KojinListDownloadParams | undefined
  >(async () => {
    return await indexedDB.kojinListDownloadParams
      .where({ userId })
      .first()
  }, [userId])

  useEffect(() => {
    if (!preParams) return
    setTarget(preParams.target)
    setFormat(preParams.format)
    setReportId(preParams.reportId)
    setColumns(preParams.columns)
  }, [preParams])

  const nFrom = useMemo(
    () => Math.max(0, from.trim() === '' ? 0 : parseInt(from)),
    [from]
  )
  const nTo = useMemo(
    () => Math.min(extendCount + targetCount,
      Math.max(0, to.trim() === '' ? (extendCount + targetCount) : parseInt(to))
    ),
    [to, extendCount, targetCount]
  )

  const validForm = useMemo(
    () => {
      if (nFrom >= nTo) return false
      if (extendCount > 0 && ['extend', 'target', 'all'].indexOf(target) < 0) return false
      if (['csv', 'tsv', 'excel', 'report', 'pptx'].indexOf(format) < 0) return false
      if (['csv', 'tsv', 'excel'].indexOf(format) >= 0 && columns.length === 0) return false
      if (format === 'report' && !KojinPrintFormatList.some((form) => form.reportId === reportId)) return false
      if (format === 'pptx' && !KojinPptxFormatList.some((form) => form.reportId === reportId)) return false
      return true
    },
    [extendCount, nFrom, nTo, target, format, reportId, columns]
  )

  const extention = useMemo(
    () => {
      if (format === 'csv') return '.csv'
      if (format === 'tsv') return '.tsv'
      if (format === 'excel') return '.xslx'
      if (format === 'report') return '.pdf'
      if (format === 'pptx') return '.pptx'
      return ''
    },
    [format]
  )

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={() => {
        if (!downloading) onClose()
      }}
    >
      {downloading && <Loading caption="download.." percent={downloadProgress ?? undefined}/>}

      <DialogTitle>
        <Stack direction="row" spacing={2}>
          <Typography variant="h5" color="text.primary" flexGrow={1}>
            ダウンロード
          </Typography>
          <Tooltip title="閉じる">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <form onSubmit={(event) => {
        event.preventDefault()
        if (!validForm) return
        const form = { userId, target, format, reportId, columns }
        onDownload(title, form, nFrom, nTo)
        indexedDB.kojinListDownloadParams.put(form)
      }}>
        <DialogContent>
          <Stack direction="column">
            {extendCount > 0 && (
              <FormControl>
                <Section label="対象" labelWidth="5em" labelProps={{ paddingY: 2 }}>
                  <RadioGroupBox
                    row
                    value={target}
                    name="download-target"
                    onChange={(e) => setTarget(e.target.value as KojinListTargetTypes)}
                    list={[
                      { value: "target", label: '元リスト', subLabel: `${numberFormat(targetCount)}件`},
                      { value: "extend", label: '補完対象', subLabel: `${numberFormat(extendCount)}件` },
                      { value: "all", label: 'すべて', subLabel: `${numberFormat(extendCount + targetCount)}件` }
                    ]}
                  />
                </Section>
              </FormControl>
            )}
            <FormControl>
              <Section label="形式" labelWidth="5em" labelProps={{ paddingY: 2 }}>
                <RadioGroup
                  row
                  value={format}
                  name="download-format"
                  onChange={(e) => setFormat(e.target.value as DownloadFormatTypes)}
                >
                  <FormControlLabel
                    value="csv"
                    control={<Radio />}
                    label="CSV形式"
                  />
                  <FormControlLabel
                    value="tsv"
                    control={<Radio />}
                    label="TSV形式"
                  />
                  <FormControlLabel
                    value="excel"
                    control={<Radio />}
                    label="Excel形式"
                  />
                  <FormControlLabel
                    value="report"
                    control={<Radio />}
                    label="レポート"
                  />
                  <FormControlLabel
                    value="pptx"
                    control={<Radio />}
                    label="PowerPoint形式"
                  />
                </RadioGroup>
              </Section>
            </FormControl>

            <FormControl>
              <Section label="内容" labelWidth="5em" labelProps={{ paddingY: 2 }}>
                {format === 'report' ? (
                  <Select
                    id="targetList"
                    variant="outlined"
                    value={reportId}
                    onChange={(event) =>
                      setReportId(event.target.value as string)
                    }
                    size="small"
                  >
                    {KojinPrintFormatList.map((reportFormat) => {
                      return (
                        <MenuItem
                          key={`reportFormat-${reportFormat.reportId}`}
                          value={reportFormat.reportId}
                        >
                          {reportFormat.reportName}
                        </MenuItem>
                      )
                    })}
                  </Select>
                ) : format === 'pptx' ? (
                  <Select
                    id="targetList"
                    variant="outlined"
                    value={reportId}
                    onChange={(event) =>
                      setReportId(event.target.value as string)
                    }
                    size="small"
                  >
                    {KojinPptxFormatList.flatMap((reportFormat) => {
                      if (reportFormat.domain && !reportFormat.domain.includes(domain)) return []
                      return (
                        <MenuItem
                          key={`reportFormat-${reportFormat.reportId}`}
                          value={reportFormat.reportId}
                        >
                          {reportFormat.reportName}
                        </MenuItem>
                      )
                    })}
                  </Select>
                ) : (
                  <Button size='small' variant="text" onClick={() => setEditMode('columns')} sx={{ textAlign: "left" }}>
                    <Stack direction='row' flexWrap='wrap' useFlexGap>
                      { columns.map((col) => {
                        const def = ColumnDefs[col];
                        return def ? <Typography key={`column-${col}`} marginRight={1} variant='body2' color='primary.text'>{def.label}</Typography> : undefined
                      })}
                    </Stack>
                  </Button>
                )}
              </Section>
            </FormControl>

            <FormControl>
              <Section label="対象範囲" labelWidth="5em" labelProps={{ paddingY: 2 }}>
                <Stack direction="row" spacing={1}>
                  <NumericInput
                    state={from}
                    setState={setFrom}
                    label="from"
                  />
                  <NumericInput
                    state={to}
                    setState={setTo}
                    label="to"
                  />
                </Stack>
              </Section>
            </FormControl>

            <FormControl>
              <Section label={`ファイル名\n／タイトル`} labelWidth="5em" labelProps={{ paddingY: 2 }}>
                <Stack direction="row" alignItems="center">
                  <TextField
                    hiddenLabel
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                  />
                  <Typography variant="body2" color="text.primary" paddingX={2}>{extention}</Typography>
                </Stack>
              </Section>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button disabled={!validForm} variant="contained" type="submit" startIcon={<OkIcon />}>
            実 行
          </Button>
          <Button variant="text" onClick={onClose}>
            キャンセル
          </Button>
        </DialogActions>
      </form>

      { editMode === 'columns' ? (
        <EditKojinListColumnsDialog
          checkedColumns={columns}
          onSubmit={(newColumns) => {
            setColumns(newColumns)
            setEditMode(null)
          }}
          onClose={() => {
            setEditMode(null)
          }}
        />
      ) : <></> }
    </Dialog>
  )
}
